import React from "react";
import { FormControl, InputLabel, MenuItem, Select, MenuItemProps, Box } from "@mui/material";
import { getIn } from "formik";

import { InputMessage, ContextualHelp, InfoTitle } from "../index";

interface IErrorMessageComponent {
  error: string;
}

export interface ISelectOption {
  value: string | number | readonly string[] | undefined;
  id: number;
  label: string;
}
interface IProps {
  placeholder: string;
  options: Array<ISelectOption>;
  required: boolean;
  label: string;
  field: {
    name: string;
    value: string;
  };
  infoTitle: string;
  form: {
    errors: {
      name: string;
    };
    touched: boolean;
  };
  isMulti: boolean;
  shrink: boolean;
  contextualHelpText: string;
  contextualHelpProps: Record<string, never>;
  ErrorMessageComponent: ({ error }: IErrorMessageComponent) => React.ReactElement;
  variant?: "filled" | "outlined" | "standard" | undefined;
  size?: "small" | "medium";
  renderMenuItem?:
    | Omit<MenuItemProps, "button">
    | ((option: ISelectOption) => Partial<MenuItemProps>);
}

const CustomSelect = ({
  placeholder,
  options = [],
  required,
  label,
  field,
  infoTitle,
  form: { errors },
  shrink,
  contextualHelpText,
  contextualHelpProps = {},
  ErrorMessageComponent = InputMessage,
  variant,
  renderMenuItem,
  ...rest
}: IProps): React.ReactElement => {
  const errorMessage = getIn(errors, field.name);
  const isError = errorMessage;

  return (
    <FormControl
      variant={variant}
      sx={{ width: "100%" }}
      {...("size" in rest && { size: rest.size })}
    >
      <InputLabel shrink={shrink} sx={{ padding: "0 5px" }}>
        {label}
      </InputLabel>

      <ContextualHelp title={contextualHelpText} {...contextualHelpProps}>
        <Select {...field} {...rest} label={label}>
          {options.map((option) => {
            const computedMenuItemProps =
              typeof renderMenuItem === "function" ? renderMenuItem(option) : renderMenuItem || {};
            return (
              <MenuItem key={option.id} value={option.value} {...computedMenuItemProps}>
                {option.label}
              </MenuItem>
            );
          })}
        </Select>
      </ContextualHelp>
      <Box
        position="absolute"
        left={0}
        top={55}
        sx={{ ...(rest.size === "small" && { top: "42px" }) }}
      >
        {isError && <ErrorMessageComponent error={errorMessage} />}
      </Box>

      {infoTitle && <InfoTitle title={infoTitle} />}
    </FormControl>
  );
};

export default CustomSelect;
